// ── Types ─────────────────────────────────────────────────────────────────

export interface MonthSelection {
  year: number;
  month: number; // 1-12
}

export interface MonthSelectorBarProps {
  selected: MonthSelection[];
  onChange: (selection: MonthSelection[]) => void;
  /** Cantidad de elementos por mes, con la clave de `monthKey`. Sin dato o en 0 no se muestra. */
  counts?: Record<string, number>;
}

export interface YearGroup {
  year: number;
  months: number[]; // 1-12
}

// ── Constants ─────────────────────────────────────────────────────────────

export const MONTH_FULL_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// ── Helpers ───────────────────────────────────────────────────────────────

/** Clave de un mes para `counts`: "2025-3". */
export const monthKey = (year: number, month: number): string => `${year}-${month}`;

/** Cuenta cuántas fechas caen en cada mes. Las vacías o inválidas no cuentan. */
export const countByMonth = (dates: (Date | string | null | undefined)[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  dates.forEach((date) => {
    if (!date) return;
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return;
    const key = monthKey(d.getFullYear(), d.getMonth() + 1);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

/**
 * Builds year groups centered on `selected`, extending `spread` months
 * in both directions.  The strip therefore always shows
 * [selected - spread … selected … selected + spread].
 *
 * Defaults to 12 months on each side (25 pills total).
 */
export const buildYearGroups = (
  selected: MonthSelection,
  spread: number = 12,
): YearGroup[] => {
  // ── Compute absolute start month ────────────────────────────────────
  let sy = selected.year;
  let sm = selected.month - spread;
  while (sm < 1)  { sm += 12; sy--; }

  // ── Compute absolute end month ──────────────────────────────────────
  let ey = selected.year;
  let em = selected.month + spread;
  while (em > 12) { em -= 12; ey++; }

  // ── Walk month by month and group by year ───────────────────────────
  const map = new Map<number, YearGroup>();
  let y = sy, m = sm;

  while (y < ey || (y === ey && m <= em)) {
    if (!map.has(y)) map.set(y, { year: y, months: [] });
    map.get(y)!.months.push(m);
    m++;
    if (m > 12) { m = 1; y++; }
  }

  return Array.from(map.values());
};

/** Returns the MonthSelection for today. */
export const todaySelection = (): MonthSelection => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

/**
 * Returns every MonthSelection between a and b (inclusive, in order).
 * Used for Shift+click range selection.
 */
export const monthsBetween = (a: MonthSelection, b: MonthSelection): MonthSelection[] => {
  // Encode as a single integer: year*12 + month
  const aVal = a.year * 12 + a.month;
  const bVal = b.year * 12 + b.month;
  const [minVal, maxVal] = aVal <= bVal ? [aVal, bVal] : [bVal, aVal];
  const results: MonthSelection[] = [];
  for (let v = minVal; v <= maxVal; v++) {
    results.push({ year: Math.floor((v - 1) / 12), month: ((v - 1) % 12) + 1 });
  }
  return results;
};
